import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { ATTRIBUTE_LABEL_DETAILS_REFERENCE_DATA_TYPES } from "@constants/reference-data";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getAttributeLabelDetailsReduxRequest } from "@redux-requests/attribute-label-details";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { AttributeLabelDetailsComponent } from "./attribute-label-details-component/attribute-label-details-component";
import { getAttributeLabelDetailsDataState } from "@redux/selectors/attribute-label-details-selectors";

export const AttributeLabelDetailsContainer = () => {
    const dispatch = useDispatch();
    const { axnAttributeLabelId } = useParams<{ axnAttributeLabelId: string }>();

    const {
        isLoadingAxnAttributeLabel,
        axnAttributeLabel
    } = useSelector(getAttributeLabelDetailsDataState);
    const referenceData = useReferenceDataSelector(ATTRIBUTE_LABEL_DETAILS_REFERENCE_DATA_TYPES);

    const [isDisabled, setIsDisabled] = useState(true);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAttributeLabel, setShouldLoadAxnAttributeLabel] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.attributeLabelDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ATTRIBUTE_LABEL_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.ATTRIBUTE_LABEL_DETAILS
            );
        }
    }, [referenceData.isLoading.attributeLabelDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAttributeTypes
        );
    }, [
        referenceData.axnAttributeTypes
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAttributeLabel && shouldLoadAxnAttributeLabel) {
            setShouldLoadAxnAttributeLabel(false);
            getAttributeLabelDetailsReduxRequest(dispatch)(axnAttributeLabelId, referenceData.axnAttributeTypes);
        }
    }, [isReferenceDataPresent, isLoadingAxnAttributeLabel, shouldLoadAxnAttributeLabel]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAttributeLabel || referenceData.isLoading.attributeLabelDetails;
    }, [isLoadingAxnAttributeLabel, referenceData.isLoading.attributeLabelDetails]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAttributeLabel &&
            isReferenceDataPresent &&
            !shouldLoadAxnAttributeLabel &&
            !shouldLoadReferenceData
        );
    }, [isLoading, axnAttributeLabel, isReferenceDataPresent, shouldLoadAxnAttributeLabel, shouldLoadReferenceData]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.attributeLabelDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <AttributeLabelDetailsComponent
                        axnAttributeLabel={axnAttributeLabel}
                        axnAttributeTypes={referenceData.axnAttributeTypes}
                        isDisabled={isDisabled}
                        setIsDisabled={setIsDisabled}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};